import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { useIsFocused } from '@react-navigation/native';

import { GET_MY_CHECKLIST_SUBMISSIONS } from '../../../common/graphql/checklist';
import { GET_MY_CLOCK_OUT_REMINDER_STATUS } from '../../../common/graphql/clockEvent';
import { GET_MY_ROSTER_SHIFTS } from '../../../common/graphql/people/roster';
import { useUserContext } from '../../../common/utils/context';
import { isDocketDoneForMyDay, isDocketHandledForMyDay } from '../../../common/utils/myDay/driverDaySteps';
import { buildFreshFoodByStop } from '../../../common/utils/pickList/buildFreshFoodByStop';
import { getLocalTime } from '../../../common/utils/time/time';
import useTodayDateKey from '../../hooks/useTodayDateKey';
import { useOfflineQueue } from '../../offline';
import {
	GET_DRIVER_MY_DAY_FRESH_FOOD,
	GET_DRIVER_MY_DAY_PICK_TOP_UP_STATUS,
	GET_DRIVER_MY_DAY_SERVICE_RUN,
	SUBSCRIBE_TO_SERVICE_DOCKET_MY_DAY,
} from './driverMyDayQueries';
import useMyClockStatus from './useMyClockStatus';
import {
	getMyDayTrainingAllowedRunIds,
	isMyDayTraining,
} from './myDayTraining';

/**
 * Options (both optional; the legacy board passes none):
 * - isFocused: replaces the screen focus that drives refetch-on-focus and the
 *   pick top-up poll, for the shared driver day provider above the screens.
 * - skip: no queries or subscription at all (users without a driver day).
 */
const useDriverMyDayData = (trainingContext = null, { isFocused: isFocusedOverride, skip = false } = {}) => {
	const isScreenFocused = useIsFocused();
	const isFocused = isFocusedOverride ?? isScreenFocused;
	const { user, tenant, isAVC } = useUserContext();
	const { queue, isOnline } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const allowedTrainingRunIds = useMemo(() => (
		getMyDayTrainingAllowedRunIds(trainingContext)
	), [trainingContext]);
	const allowedTrainingRunIdSet = useMemo(() => (
		new Set(allowedTrainingRunIds)
	), [allowedTrainingRunIds]);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const [selectedRunId, setSelectedRunId] = useState(null);
	const [clockOutReminderNow, setClockOutReminderNow] = useState(() => Date.now());
	const hasFocusedScreenOnceRef = useRef(false);
	const previousPickTopUpStatusRef = useRef(null);

	const timezone = tenant?.timezone;
	// Rolls over at midnight and on returning to the app: the shared driver day stays mounted overnight.
	const todayDateKey = useTodayDateKey(timezone);
	const targetDateKey = isTraining ? trainingContext.dateKey : todayDateKey;
	const pendingConfirmStockDocketIds = useMemo(() => new Set((scopedQueue || [])
		.filter(entry => (
			entry.configKey === 'confirmStockLevels' &&
			entry.meta?.serviceDocketId &&
			!entry.failureCount
		))
		.map(entry => entry.meta.serviceDocketId)), [scopedQueue]);
	const isDocketDone = useCallback(docket => isDocketDoneForMyDay(docket, {
		hasPendingConfirmStockSync: pendingConfirmStockDocketIds.has(docket?.id),
	}), [pendingConfirmStockDocketIds]);
	// Done, or marked "Couldn't complete": the driver has moved on from it.
	const isDocketHandled = useCallback(docket => isDocketHandledForMyDay(docket, {
		hasPendingConfirmStockSync: pendingConfirmStockDocketIds.has(docket?.id),
	}), [pendingConfirmStockDocketIds]);

	const driverFilter = useMemo(() => {
		if (skip) {
			return null;
		}

		if (isTraining) {
			return {
				serviceRunIds: allowedTrainingRunIds,
				driver: trainingContext.targetDriverId,
			};
		}

		if (!user?.id) {
			return null;
		}

		// Include yesterday onward to tolerate timezone boundaries in backend day filtering.
		// Final run selection is handled client-side and never chooses a past day.
		const queryStartUnix = getLocalTime(timezone)
			.subtract(1, 'day')
			.startOf('day')
			.unix();

		return {
			driver: user.id,
			plannedFromDate: queryStartUnix,
		};
		// todayDateKey moves the query window forward when the day rolls over.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allowedTrainingRunIds, isTraining, skip, timezone, todayDateKey, trainingContext?.targetDriverId, user?.id]);

	const {
		data: runsData,
		loading: runsLoading,
		error: runsError,
		refetch: refetchRuns,
	} = useQuery(GET_DRIVER_MY_DAY_SERVICE_RUN, {
		variables: {
			page: 0,
			perPage: 50,
			sortBy: 'planned',
			sortDirection: 1,
			includeTrainingContext: isTraining,
			filter: driverFilter,
		},
		skip: !driverFilter,
		// Cached board must stay usable offline (dead zones mid-run); the
		// network part still refreshes it on every mount and focus, and a
		// failed refresh must not blank cached data.
		fetchPolicy: isTraining ? 'no-cache' : 'cache-and-network',
		errorPolicy: 'all',
	});

	useSubscription(SUBSCRIBE_TO_SERVICE_DOCKET_MY_DAY, {
		variables: {
			input: {},
		},
		skip: skip || isTraining || Boolean(user?.settings?.performanceMode),
	});

	const { clockStatus: selfClockStatus, refetch: refetchClockStatus } = useMyClockStatus({
		skip: skip || isTraining,
		isFocused: isFocusedOverride,
	});
	const {
		data: clockOutReminderData,
		refetch: refetchClockOutReminder,
	} = useQuery(GET_MY_CLOCK_OUT_REMINDER_STATUS, {
		skip: skip || !isAVC || isTraining,
		fetchPolicy: 'cache-and-network',
		errorPolicy: 'all',
	});
	const rawClockOutReminder = clockOutReminderData?.getMyClockOutReminderStatus || null;
	useEffect(() => {
		const dueAt = new Date(rawClockOutReminder?.reminderDueAt).getTime();
		const delay = dueAt - Date.now();
		if (!Number.isFinite(dueAt) || rawClockOutReminder?.reminderActive) {
			return undefined;
		}
		if (delay <= 0) {
			setClockOutReminderNow(Date.now());
			return undefined;
		}

		const timeout = setTimeout(() => setClockOutReminderNow(Date.now()), delay);
		return () => clearTimeout(timeout);
	}, [rawClockOutReminder?.reminderActive, rawClockOutReminder?.reminderDueAt]);
	const clockOutReminder = useMemo(() => {
		if (!rawClockOutReminder) {
			return null;
		}
		const dueAt = new Date(rawClockOutReminder.reminderDueAt).getTime();
		return {
			...rawClockOutReminder,
			reminderActive: Boolean(rawClockOutReminder.reminderActive || (
				rawClockOutReminder.returnedToWarehouseAt &&
				Number.isFinite(dueAt) &&
				dueAt <= clockOutReminderNow
			)),
		};
	}, [clockOutReminderNow, rawClockOutReminder]);

	const {
		data: rosterData,
		refetch: refetchRosterShifts,
	} = useQuery(GET_MY_ROSTER_SHIFTS, {
		variables: {
			fromDate: todayDateKey,
			toDate: todayDateKey,
		},
		// Roster is AVC people-gated; errors must not block the board.
		skip: skip || !isAVC || isTraining,
		errorPolicy: 'all',
	});

	const {
		data: checklistData,
		refetch: refetchChecklistSubmissions,
	} = useQuery(GET_MY_CHECKLIST_SUBMISSIONS, {
		variables: {
			date: todayDateKey,
		},
		fetchPolicy: 'cache-and-network',
		errorPolicy: 'all',
		skip: skip || isTraining,
	});

	const { todaysRuns, runsUsedForDayCompletion, upcomingRun } = useMemo(() => {
		const serviceRuns = (runsData?.getServiceRuns?.items || []).filter(run => (
			!isTraining || (
				allowedTrainingRunIdSet.has(String(run?.id)) &&
				String(run?.driver?.id) === String(trainingContext.targetDriverId)
			)
		));
		const runsWithDateKeys = serviceRuns.map(item => ({
			item,
			plannedDateKey: item?.planned ? getLocalTime(timezone, item.planned).format('YYYY-MM-DD') : null,
		}));

		const runsToday = runsWithDateKeys
			.filter(run => run.plannedDateKey === targetDateKey && (!isTraining || targetDateKey === todayDateKey))
			.map(run => run.item)
			.sort((runA, runB) => new Date(runA.planned) - new Date(runB.planned));

		const futureRun = isTraining ? null : runsWithDateKeys
			.filter(run => run.plannedDateKey && run.plannedDateKey > todayDateKey)
			.map(run => run.item)[0] || null;

		const engagedRuns = isTraining
			? runsToday.filter(run => String(run?.id) === String(trainingContext.runId))
			: runsToday;

		return {
			todaysRuns: engagedRuns,
			runsUsedForDayCompletion: runsToday,
			upcomingRun: runsToday.length ? null : futureRun,
		};
	}, [
		allowedTrainingRunIdSet,
		isTraining,
		runsData?.getServiceRuns?.items,
		targetDateKey,
		timezone,
		todayDateKey,
		trainingContext?.runId,
		trainingContext?.targetDriverId,
	]);

	const activeRun = useMemo(() => {
		if (!todaysRuns.length) {
			return null;
		}

		if (selectedRunId) {
			const selectedRun = todaysRuns.find(run => run.id === selectedRunId);
			if (selectedRun) {
				return selectedRun;
			}
		}

		const firstIncompleteRun = todaysRuns.find(run => (
			(run.serviceDockets || []).some(docket => !isDocketHandled(docket))
		));

		return firstIncompleteRun || todaysRuns[0];
	}, [isDocketHandled, selectedRunId, todaysRuns]);
	// Stops marked "Couldn't complete" count as behind the driver, so the day moves on to end of day.
	const allAssignedRunsComplete = useMemo(() => (
		runsUsedForDayCompletion.length > 0 &&
		(!isTraining || runsUsedForDayCompletion.length === allowedTrainingRunIds.length) &&
		runsUsedForDayCompletion.every(run => (
			(run?.serviceDockets || []).length > 0 &&
			(run.serviceDockets || []).every(isDocketHandled)
		))
	), [allowedTrainingRunIds.length, isDocketHandled, isTraining, runsUsedForDayCompletion]);
	// Day-level "machines done vs assigned" fill for the My Day header avatar.
	// Counted across every assigned run today (not the active run) so the fill
	// spans the whole day and never resets when the driver switches runs, and
	// via isDocketDone so it stays accurate offline from the cached board.
	// The 0/0 edge (assigned runs with no stops yet) reports 0 fill and not
	// complete so the header can hide the glyph rather than render NaN.
	const machineFill = useMemo(() => {
		let totalStops = 0;
		let doneStops = 0;
		runsUsedForDayCompletion.forEach(run => {
			(run?.serviceDockets || []).forEach(docket => {
				totalStops += 1;
				if (isDocketDone(docket)) {
					doneStops += 1;
				}
			});
		});

		return {
			totalStops,
			doneStops,
			ratio: totalStops > 0 ? doneStops / totalStops : 0,
			isComplete: totalStops > 0 && doneStops === totalStops,
		};
	}, [isDocketDone, runsUsedForDayCompletion]);
	const targetClockSummary = isTraining ? activeRun?.driverClockSummary : null;
	const targetClockStatus = useMemo(() => ({
		isClockedIn: Boolean(targetClockSummary?.isClockedIn ?? (
			targetClockSummary?.clockedInAt && !targetClockSummary?.clockedOutAt
		)),
		hasClockedOutToday: Boolean(targetClockSummary?.clockedOutAt),
	}), [targetClockSummary]);
	const clockStatus = isTraining ? targetClockStatus : selfClockStatus;
	const trainingClockOutReminder = useMemo(() => (targetClockSummary ? {
		returnedToWarehouseAt: targetClockSummary.returnedToWarehouseAt || null,
		reminderDueAt: targetClockSummary.reminderDueAt || null,
		reminderResolvedAt: targetClockSummary.reminderResolvedAt || null,
		reminderActive: false,
		canConfirmReturnedToWarehouse: false,
	} : null), [targetClockSummary]);
	const resolvedClockOutReminder = isTraining ? trainingClockOutReminder : clockOutReminder;
	const hasActiveRunWork = (activeRun?.serviceDockets || []).some(docket => !isDocketHandled(docket));
	const {
		data: pickTopUpStatusData,
		refetch: refetchPickTopUpStatus,
	} = useQuery(GET_DRIVER_MY_DAY_PICK_TOP_UP_STATUS, {
		variables: { id: activeRun?.id, ...(isTraining ? { adminTraining: true } : {}) },
		skip: !activeRun?.id,
		fetchPolicy: isTraining ? 'no-cache' : 'cache-and-network',
		errorPolicy: 'all',
		// Docket subscriptions refresh most changes. This small active-run query
		// also catches later sales without repeatedly loading the full My day board.
		pollInterval: isFocused && isOnline && hasActiveRunWork ? 30000 : 0,
	});
	const activeRunPickTopUpStatus = (
		activeRun?.id && pickTopUpStatusData?.getServiceRun?.id === activeRun.id
			? pickTopUpStatusData.getServiceRun.myDayPickTopUpStatus
			: null
	);
	const activeRunWithPickTopUpStatus = useMemo(() => (
		activeRun && activeRunPickTopUpStatus
			? { ...activeRun, myDayPickTopUpStatus: activeRunPickTopUpStatus }
			: activeRun
	), [activeRun, activeRunPickTopUpStatus]);
	const {
		data: freshFoodData,
		refetch: refetchFreshFood,
	} = useQuery(GET_DRIVER_MY_DAY_FRESH_FOOD, {
		variables: { id: activeRun?.id },
		skip: !activeRun?.id,
		fetchPolicy: isTraining ? 'no-cache' : 'cache-and-network',
		errorPolicy: 'all',
	});
	const freshFoodStops = useMemo(() => (
		activeRun?.id && freshFoodData?.getServiceRun?.id === activeRun.id
			? buildFreshFoodByStop((freshFoodData.getServiceRun.serviceDockets || []).map(({ freshFoodPickList, ...serviceDocket }) => ({
				...serviceDocket,
				pickList: freshFoodPickList,
			})))
			: []
	), [activeRun?.id, freshFoodData]);
	const refetchRunData = useCallback(() => {
		const requests = [refetchRuns()];
		if (activeRun?.id && isOnline) {
			requests.push(refetchPickTopUpStatus());
			requests.push(refetchFreshFood());
		}
		if (!isTraining && isAVC && isOnline) {
			requests.push(refetchClockOutReminder());
		}

		return Promise.all(requests);
	}, [activeRun?.id, isAVC, isOnline, isTraining, refetchClockOutReminder, refetchFreshFood, refetchPickTopUpStatus, refetchRuns]);
	useEffect(() => {
		if (!activeRun?.id || !activeRunPickTopUpStatus) {
			return;
		}

		const previousStatus = previousPickTopUpStatusRef.current;
		if (
			isOnline &&
			previousStatus?.runId === activeRun.id &&
			previousStatus.markedNotNeeded &&
			!activeRunPickTopUpStatus.markedNotNeeded
		) {
			// The small readiness poll detected that new work, pick state or sales
			// invalidated the decision. Refresh once so the full board shows the cause.
			refetchRuns();
		}
		previousPickTopUpStatusRef.current = {
			runId: activeRun.id,
			markedNotNeeded: Boolean(activeRunPickTopUpStatus.markedNotNeeded),
		};
	}, [activeRun, activeRunPickTopUpStatus, isOnline, refetchRuns]);

	useEffect(() => {
		if (!isFocused || !driverFilter) {
			return;
		}

		if (!hasFocusedScreenOnceRef.current) {
			hasFocusedScreenOnceRef.current = true;
			return;
		}

		refetchRunData();
		if (!isTraining) {
			refetchChecklistSubmissions();
		}
	}, [driverFilter, isFocused, isTraining, refetchChecklistSubmissions, refetchRunData]);

	const setActiveRunId = useCallback((runId) => {
		setSelectedRunId(runId);
	}, []);

	const rosterShift = useMemo(() => {
		const shifts = rosterData?.getMyRosterShifts || [];
		return shifts.find(shift => shift?.date === todayDateKey) || null;
	}, [rosterData?.getMyRosterShifts, todayDateKey]);

	const checklistSubmissions = useMemo(() => (
		isTraining
			? activeRun?.checklistSubmissions || []
			: checklistData?.getMyChecklistSubmissions || []
	), [activeRun?.checklistSubmissions, checklistData?.getMyChecklistSubmissions, isTraining]);

	const pendingChecklistPurposes = useMemo(() => {
		const purposes = (scopedQueue || [])
			.filter(entry => (
				entry.configKey === 'submitChecklist' &&
				entry.meta?.date === todayDateKey &&
				entry.meta?.purpose
			))
			.map(entry => Number(entry.meta.purpose));

		return new Set(purposes);
	}, [scopedQueue, todayDateKey]);

	const refetchAll = useCallback(() => {
		if (isTraining) {
			return refetchRunData();
		}
		return Promise.all([
			refetchRunData(),
			refetchClockStatus(),
			refetchChecklistSubmissions(),
			...(isAVC ? [refetchRosterShifts()] : []),
		]);
	}, [isAVC, isTraining, refetchChecklistSubmissions, refetchClockStatus, refetchRosterShifts, refetchRunData]);

	return {
		todaysRuns,
		activeRun: activeRunWithPickTopUpStatus,
		freshFoodStops,
		setActiveRunId,
		upcomingRun,
		clockStatus,
		clockOutReminder: resolvedClockOutReminder,
		allAssignedRunsComplete,
		machineFill,
		rosterShift,
		checklistSubmissions,
		pendingChecklistPurposes,
		isDocketDone,
		isDocketHandled,
		todayDateKey,
		// Only the true first load should blank the board; cache-and-network
		// keeps loading true during background refreshes over cached data.
		loading: runsLoading && !runsData,
		// A failed load with no usable run list (nothing cached, or a resolver
		// error returning null); a cached list stays usable offline.
		loadError: runsData?.getServiceRuns?.items ? null : runsError || null,
		refetchRuns: refetchRunData,
		refetchAll,
	};
};

export default useDriverMyDayData;
